import { useCallback, useEffect, useRef } from "react";

import type {
  ReferralIncentive,
  ReferralReward,
} from "../lib/referral-links-api";
import { useReferralStore } from "../stores/referral";

export interface UseConsumerReferralState {
  /** The real, minted share URL. Null while loading or on error — never a fallback/fake link. */
  shareUrl: string | null;
  /** Number of friends attributed to the caller's links. Null while loading or on error. */
  signupCount: number | null;
  /** Reward ledger rows. Empty when nothing to show yet (no monetary incentive has earned). */
  rewards: ReferralReward[];
  /** 1-based waitlist rank, or null while the area's rank is unrevealed / the caller is not queued. */
  waitlistPosition: number | null;
  /** Friends whose publish actually advanced the caller's waitlist position — the always-on reward. Null while loading or on error. */
  waitlistCredits: number | null;
  /** The LIVE monetary incentive (validated, enabled), or null when there is nothing to promise. Null while loading or on error. */
  incentive: ReferralIncentive | null;
  /** True only before the first response (success or failure) arrives. */
  isLoading: boolean;
  /** Set on any fetch/mint failure. The caller must show a truthful error, never fabricated zeros. */
  error: Error | null;
  /** Clears the error and re-issues the request. */
  retry: () => void;
}

/**
 * Reads the caller's consumer_referral share link plus their live
 * signup/reward/incentive summary through the shared `useReferralStore`
 * (one request per session, shared across every surface — see the store for
 * why). The public shape is unchanged from the per-hook implementation it
 * replaced; StrictMode double-mounts and concurrently mounting surfaces both
 * collapse onto the store's single in-flight promise.
 *
 * `getToken` is held in a ref so Clerk's unstable identity never re-triggers
 * the load effect (the useWaitlist precedent).
 */
const NO_REWARDS: ReferralReward[] = [];

export function useConsumerReferral(
  getToken: () => Promise<string | null>,
): UseConsumerReferralState {
  const summary = useReferralStore((s) => s.summary);
  const error = useReferralStore((s) => s.error);
  const hasLoaded = useReferralStore((s) => s.hasLoaded);
  const isFetching = useReferralStore((s) => s.isFetching);
  const ensureLoaded = useReferralStore((s) => s.ensureLoaded);
  const refresh = useReferralStore((s) => s.refresh);
  const tokenRef = useRef(getToken);

  useEffect(() => {
    tokenRef.current = getToken;
  }, [getToken]);

  useEffect(() => {
    void ensureLoaded(() => tokenRef.current());
  }, [ensureLoaded]);

  const retry = useCallback(() => {
    void refresh(() => tokenRef.current());
  }, [refresh]);

  // "Loading" = nothing has settled yet (no success, no error). A retry after
  // a failure clears the error, so it reads as loading again until it settles.
  const isLoading = !hasLoaded && (error === null || isFetching);

  return {
    shareUrl: summary?.shareUrl ?? null,
    signupCount: summary?.signupCount ?? null,
    rewards: summary?.rewards ?? NO_REWARDS,
    waitlistPosition: summary?.waitlistPosition ?? null,
    waitlistCredits: summary?.waitlistCredits ?? null,
    incentive: summary?.incentive ?? null,
    isLoading,
    error,
    retry,
  };
}
